import { type ReactNode } from "react";
import { useMyDialog } from "../context/MyDialogProvider";

interface MyDialogButtonProps {
  label?: ReactNode;
  icon?: ReactNode;
  dialogContent: ReactNode;
  dialogHeader?: ReactNode | (() => ReactNode);
  variant?: "modal" | "sheet";
  padding?: string;
  rounded?: string;
  bgColor?: string;
  hoverColor?: string;
  textColor?: string;
  style?: string;
  textButtonClassName?: string;
  textButton?: boolean;
  topSmallLabel?: string;
  button?: ReactNode;
  route?: string;              // optional: URL for dialog routing
  fullScreen?: boolean;        // new: opens dialog near full screen
  initialSnap?: number;        // new: initial height (0-1) for non-fullscreen
}

export default function MyDialogButton({
  label = "Open",
  variant = "sheet", 
  fullScreen,
  textButton = false,
  textButtonClassName = "text-green-800 dark:text-green-200 underline font-bold",
  icon,
  dialogContent,
  dialogHeader,
  style="",
  padding = "p-2",
  rounded = "rounded-xl",
  bgColor = "bg-green-600",
  hoverColor = "hover:scale-102",
  textColor = "text-gray-600 dark:text-gray-300",
  topSmallLabel,
  button,
  route,
  initialSnap = 0.9,
}: MyDialogButtonProps) {
  const { open } = useMyDialog();

  const handleOpen = () => {
    const header =
      typeof dialogHeader === "function" ? dialogHeader() : dialogHeader;

    open({
      variant,
      content: dialogContent,
      header,
      route,
      fullScreen,
      initialSnap,
    });
  };

  if (textButton) {
    return (
      <button
        type="button"
        className={textButtonClassName}
        onClick={handleOpen}
        aria-haspopup="dialog"
      >
        {label}
      </button>
    );
  }

  if (button) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => e.key === "Enter" && handleOpen()}
        className="cursor-pointer"
        aria-haspopup="dialog"
      >
        {button}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-haspopup="dialog"
      className={` relative  flex justify-center text-white shadow-xl dark:border dark:border-gray-600 ${style} ${padding} ${bgColor} ${hoverColor} ${textColor} ${rounded} border border-gray-200 font-semibold transition flex items-center gap-2`}
    >
      {topSmallLabel && (
        <div className="absolute -top-5 text-[11px] bg-white dark:bg-black/60 text-gray-400 px-2 rounded-full">
          {topSmallLabel}
        </div>
      )}
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );
}